/**
 * Account metro — the city the renter picked has to survive a new browser.
 *
 * localStorage (`taco-metro`) is only this device. The account field is
 * `users.preferred_city`. Restore it once per signed-in user after /auth/me
 * (or login) and do not invent Austin before that check finishes.
 */
import { CITY_ROWS, DEFAULT_METRO } from '../config/cityRows';
import { useMetroStore } from './metroStore';

const CANONICAL = new Set(CITY_ROWS.map((row) => row.key));

const ALIASES: Record<string, string> = {
  austin: 'Austin',
  dallas: 'Dallas',
  'dallas / fort worth': 'Dallas',
  'dallas/fort worth': 'Dallas',
  dfw: 'Dallas',
  houston: 'Houston',
  'san antonio': 'San Antonio',
  'san-antonio': 'San Antonio',
};

export function knownMetro(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (CANONICAL.has(trimmed)) return trimmed;
  return ALIASES[trimmed.toLowerCase()] || null;
}

/**
 * What the shell should show before the account is known.
 * A missing stored city stays unset — Austin is only the post-auth fallback.
 */
export function metroBeforeAuth(storedCity: string | null): string | null {
  return storedCity;
}

export function metroAfterAuth(storedCity: string | null, accountCity: unknown): string {
  return knownMetro(accountCity) || storedCity || DEFAULT_METRO;
}

let hydratedUserId: string | null = null;

function userIdOf(user: { user_id?: string; id?: string } | null | undefined): string | null {
  if (!user) return null;
  return user.user_id || user.id || null;
}

/** Apply the account city once per user id. Later in-session picks win. */
export function restoreAccountMetro(user: { user_id?: string; id?: string; preferred_city?: unknown } | null | undefined): void {
  const id = userIdOf(user);
  if (!id || hydratedUserId === id) return;
  hydratedUserId = id;
  const city = knownMetro(user?.preferred_city);
  if (city) useMetroStore.getState().setSelectedMetro(city);
}

export function clearAccountMetroHydration(): void {
  hydratedUserId = null;
}

/** Header and hero picks. Signed-in renters also write the account. */
export function rememberMetro(metro: string): void {
  const city = knownMetro(metro);
  if (!city) return;
  useMetroStore.getState().setSelectedMetro(city);
  void persistAccountMetro(city);
}

async function persistAccountMetro(city: string): Promise<void> {
  // Loaded on use so this module can be imported by the auth store.
  let getState: (() => {
    user?: { preferred_city?: unknown } | null;
    updateProfile?: (profile: Record<string, unknown>) => Promise<boolean>;
  }) | undefined;
  try {
    const mod = await import('./authStore');
    getState = mod.useAuthStore.getState as typeof getState;
  } catch {
    return;
  }
  if (typeof getState !== 'function') return;
  const auth = getState();
  const preferred = auth?.user ? (auth.user as { preferred_city?: unknown }).preferred_city : undefined;
  if (!auth?.user || typeof auth.updateProfile !== 'function') return;
  if (knownMetro(preferred) === city) return;
  await auth.updateProfile({ preferred_city: city });
}
